import { useState } from "react";
import { useQueryParam } from "../../_app/hooks";
import { getSensitiveParam } from "../lib/sensitiveParams";
import { ChangePasswordForm } from "../types";
import { confirmResetPassword } from "aws-amplify/auth";
import AuthLayout from "../layouts/AuthLayout";
import StatusMessage from "../components/cognito/StatusMessage";
import CognitoPasswordChangeForm from "../components/cognito/CognitoPasswordChangeForm";
import { useCognitoUserpoolPolicy } from "../hooks";
import { useAuthenticator } from "@aws-amplify/ui-react";

type ForgotPasswordState = {
  type: "success" | "error";
  message: string;
};

const ResetPassword = () => {
  const [forgotPasswordState, setForgotPasswordState] = useState<ForgotPasswordState | null>(null);
  // Prefer the stashed value (stripped from the URL at bootstrap); fall back to the query param.
  const usernameParam = useQueryParam("username") as string;
  const codeParam = useQueryParam("code") as string;
  const username = getSensitiveParam("username") ?? usernameParam;
  const code = getSensitiveParam("code") ?? codeParam;
  const { data: passwordPolicy, isFetching } = useCognitoUserpoolPolicy();
  const { signOut } = useAuthenticator((context) => [context.user]);

  async function handleChangePassword(form: ChangePasswordForm) {
    signOut();
    try {
      await confirmResetPassword({
        username: username,
        newPassword: form.password,
        confirmationCode: code,
      });
      setForgotPasswordState({
        type: "success",
        message: "Your password has been changed. You can now log in with your new password.",
      });
    } catch (error) {
      if (error instanceof Error && error.name == "ExpiredCodeException") {
        setForgotPasswordState({
          type: "error",
          message: "It looks like the password reset link has expired. Please request a new link to reset your password.",
        });
      } else {
        setForgotPasswordState({
          type: "error",
          message: "We couldn't complete the password change. Please try again, or reach out to support if the issue continues.",
        });
      }
    }
  }

  const renderContent = () => {
    if (forgotPasswordState !== null) {
      return (
        <StatusMessage
          type={forgotPasswordState?.type || "success"}
          message={forgotPasswordState?.message || "Something went wrong. Please try again."}
        />
      );
    }
    return <CognitoPasswordChangeForm heading="Reset password" passwordPolicy={passwordPolicy} onSubmit={handleChangePassword} />;
  };

  return (
    <AuthLayout title="Reset Password" isLoading={isFetching}>
      {renderContent()}
    </AuthLayout>
  );
};

export default ResetPassword;
